/**
 * Fluid cursor effect — public API
 * Inspired by PavelDoGreat/WebGL-Fluid-Simulation and faraasat/smokey-fluid-cursor
 * (minimal extracted implementation)
 *
 * Usage:
 *   import { init, destroy, setEnabled } from './effects/fluid';
 *   const handle = init(canvasElement);
 *   setEnabled(handle, false);   // pause
 *   destroy(handle);             // clean up
 */

import FLUID_CONFIG from './config';
import {
  initWebGL,
  createFBO,
  createDoubleFBO,
  compileShader,
  createProgram,
  getUniforms,
  createBlit,
} from './gl';
import {
  baseVertexShader,
  clearShader,
  splatShader,
  advectionShader,
  divergenceShader,
  curlShader,
  vorticityShader,
  pressureShader,
  gradientSubtractShader,
  displayShader,
} from './shaders';
import {
  scaleByPixelRatio,
  getResolution,
  correctDeltaX,
  correctDeltaY,
  correctRadius,
} from './utils';

// ---------- Material (shader program wrapper with keyword variants) ----------

class Material {
  constructor(gl, vertexShader, fragmentShaderSource) {
    this.gl = gl;
    this.vertexShader = vertexShader;
    this.fragmentShaderSource = fragmentShaderSource;
    this.programs = {};
    this.activeProgram = null;
    this.uniforms = {};
  }

  bind(keywords) {
    const key = keywords ? keywords.join('-') : '';
    if (!this.programs[key]) {
      const fs = compileShader(
        this.gl,
        this.gl.FRAGMENT_SHADER,
        this.fragmentShaderSource,
        keywords
      );
      if (!fs) return;
      const prog = createProgram(this.gl, this.vertexShader, fs);
      if (!prog) return;
      this.programs[key] = prog;
    }
    if (this.programs[key] !== this.activeProgram) {
      this.activeProgram = this.programs[key];
      this.gl.useProgram(this.activeProgram);
      this.uniforms = getUniforms(this.gl, this.activeProgram);
    }
  }
}

class Program {
  constructor(gl, vertexShader, fragmentShader) {
    this.gl = gl;
    this.program = createProgram(gl, vertexShader, fragmentShader);
    this.uniforms = this.program ? getUniforms(gl, this.program) : {};
  }

  bind() {
    this.gl.useProgram(this.program);
  }
}

// ---------- Init ----------

export function init(canvas) {
  const ctx = initWebGL(canvas);
  if (!ctx) {
    console.warn('[FluidCursor] WebGL not supported');
    return null;
  }

  const { gl, halfFloatTexType, supportLinearFiltering, formatRGBA, formatRG, formatR } = ctx;
  const config = { ...FLUID_CONFIG };

  const baseVS = compileShader(gl, gl.VERTEX_SHADER, baseVertexShader);
  if (!baseVS) return null;

  const filterKeywords = supportLinearFiltering ? [] : ['MANUAL_FILTERING'];

  const clearProg = new Program(gl, baseVS, compileShader(gl, gl.FRAGMENT_SHADER, clearShader));
  const splatProg = new Program(gl, baseVS, compileShader(gl, gl.FRAGMENT_SHADER, splatShader));
  const advectionProg = new Program(gl, baseVS, compileShader(gl, gl.FRAGMENT_SHADER, advectionShader, filterKeywords));
  const divergenceProg = new Program(gl, baseVS, compileShader(gl, gl.FRAGMENT_SHADER, divergenceShader));
  const curlProg = new Program(gl, baseVS, compileShader(gl, gl.FRAGMENT_SHADER, curlShader));
  const vorticityProg = new Program(gl, baseVS, compileShader(gl, gl.FRAGMENT_SHADER, vorticityShader));
  const pressureProg = new Program(gl, baseVS, compileShader(gl, gl.FRAGMENT_SHADER, pressureShader));
  const gradSubProg = new Program(gl, baseVS, compileShader(gl, gl.FRAGMENT_SHADER, gradientSubtractShader));
  const displayKeywords = config.SHADING ? ['SHADING'] : [];
  const displayMat = new Material(gl, baseVS, displayShader);

  const blit = createBlit(gl);
  const texType = halfFloatTexType;
  const filtering = supportLinearFiltering ? gl.LINEAR : gl.NEAREST;

  const simRes = getResolution(gl, config.SIM_RESOLUTION);
  const dyeRes = getResolution(gl, config.DYE_RESOLUTION);

  let dye = createDoubleFBO(gl, dyeRes.width, dyeRes.height, formatRGBA.internalFormat, formatRGBA.format, texType, filtering);
  let velocity = createDoubleFBO(gl, simRes.width, simRes.height, formatRG.internalFormat, formatRG.format, texType, filtering);
  let divergenceFBO = createFBO(gl, simRes.width, simRes.height, formatR.internalFormat, formatR.format, texType, gl.NEAREST);
  let curlFBO = createFBO(gl, simRes.width, simRes.height, formatR.internalFormat, formatR.format, texType, gl.NEAREST);
  let pressure = createDoubleFBO(gl, simRes.width, simRes.height, formatR.internalFormat, formatR.format, texType, gl.NEAREST);

  // Mutable state (closure-scoped)
  const state = { enabled: true, rafId: null, lastTime: Date.now() };

  const pointer = {
    texcoordX: 0, texcoordY: 0,
    prevTexcoordX: 0, prevTexcoordY: 0,
    deltaX: 0, deltaY: 0,
    moved: false,
  };

  // ---------- Pointer ----------

  function onMouseMove(e) {
    if (!state.enabled) return;
    const rect = canvas.getBoundingClientRect();
    const sx = scaleByPixelRatio(e.clientX - rect.left, config.MAX_DPR);
    const sy = scaleByPixelRatio(e.clientY - rect.top, config.MAX_DPR);
    pointer.prevTexcoordX = pointer.texcoordX;
    pointer.prevTexcoordY = pointer.texcoordY;
    pointer.texcoordX = sx / canvas.width;
    pointer.texcoordY = 1.0 - sy / canvas.height;
    pointer.deltaX = correctDeltaX(pointer.texcoordX - pointer.prevTexcoordX, canvas);
    pointer.deltaY = correctDeltaY(pointer.texcoordY - pointer.prevTexcoordY, canvas);
    pointer.moved = Math.abs(pointer.deltaX) > 0 || Math.abs(pointer.deltaY) > 0;
  }

  window.addEventListener('mousemove', onMouseMove, { passive: true });

  // ---------- Visibility ----------

  function onVisibility() {
    if (document.hidden) {
      if (state.rafId) { cancelAnimationFrame(state.rafId); state.rafId = null; }
    } else if (state.enabled && !state.rafId) {
      state.lastTime = Date.now();
      state.rafId = requestAnimationFrame(loop);
    }
  }

  document.addEventListener('visibilitychange', onVisibility);

  // ---------- Resize ----------

  function onResize() {
    const dpr = Math.min(window.devicePixelRatio || 1, config.MAX_DPR);
    const w = Math.floor(canvas.clientWidth * dpr);
    const h = Math.floor(canvas.clientHeight * dpr);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      const sr = getResolution(gl, config.SIM_RESOLUTION);
      const dr = getResolution(gl, config.DYE_RESOLUTION);
      dye = createDoubleFBO(gl, dr.width, dr.height, formatRGBA.internalFormat, formatRGBA.format, texType, filtering);
      velocity = createDoubleFBO(gl, sr.width, sr.height, formatRG.internalFormat, formatRG.format, texType, filtering);
      divergenceFBO = createFBO(gl, sr.width, sr.height, formatR.internalFormat, formatR.format, texType, gl.NEAREST);
      curlFBO = createFBO(gl, sr.width, sr.height, formatR.internalFormat, formatR.format, texType, gl.NEAREST);
      pressure = createDoubleFBO(gl, sr.width, sr.height, formatR.internalFormat, formatR.format, texType, gl.NEAREST);
    }
  }

  window.addEventListener('resize', onResize, { passive: true });
  onResize();

  // ---------- Simulation step ----------

  function step(dt) {
    gl.disable(gl.BLEND);

    curlProg.bind();
    gl.uniform2f(curlProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(curlProg.uniforms.uVelocity, velocity.read.attach(0));
    blit(curlFBO);

    vorticityProg.bind();
    gl.uniform2f(vorticityProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(vorticityProg.uniforms.uVelocity, velocity.read.attach(0));
    gl.uniform1i(vorticityProg.uniforms.uCurl, curlFBO.attach(1));
    gl.uniform1f(vorticityProg.uniforms.curl, config.CURL);
    gl.uniform1f(vorticityProg.uniforms.dt, dt);
    blit(velocity.write);
    velocity.swap();

    divergenceProg.bind();
    gl.uniform2f(divergenceProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(divergenceProg.uniforms.uVelocity, velocity.read.attach(0));
    blit(divergenceFBO);

    clearProg.bind();
    gl.uniform1i(clearProg.uniforms.uTexture, pressure.read.attach(0));
    gl.uniform1f(clearProg.uniforms.value, config.PRESSURE);
    blit(pressure.write);
    pressure.swap();

    pressureProg.bind();
    gl.uniform2f(pressureProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(pressureProg.uniforms.uDivergence, divergenceFBO.attach(0));
    for (let i = 0; i < config.PRESSURE_ITERATIONS; i++) {
      gl.uniform1i(pressureProg.uniforms.uPressure, pressure.read.attach(1));
      blit(pressure.write);
      pressure.swap();
    }

    gradSubProg.bind();
    gl.uniform2f(gradSubProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(gradSubProg.uniforms.uPressure, pressure.read.attach(0));
    gl.uniform1i(gradSubProg.uniforms.uVelocity, velocity.read.attach(1));
    blit(velocity.write);
    velocity.swap();

    advectionProg.bind();
    gl.uniform2f(advectionProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform2f(advectionProg.uniforms.dyeTexelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(advectionProg.uniforms.uVelocity, velocity.read.attach(0));
    gl.uniform1i(advectionProg.uniforms.uSource, velocity.read.attach(0));
    gl.uniform1f(advectionProg.uniforms.dt, dt);
    gl.uniform1f(advectionProg.uniforms.dissipation, config.VELOCITY_DISSIPATION);
    blit(velocity.write);
    velocity.swap();

    advectionProg.bind();
    gl.uniform2f(advectionProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform2f(advectionProg.uniforms.dyeTexelSize, dye.texelSizeX, dye.texelSizeY);
    gl.uniform1i(advectionProg.uniforms.uVelocity, velocity.read.attach(0));
    gl.uniform1i(advectionProg.uniforms.uSource, dye.read.attach(1));
    gl.uniform1f(advectionProg.uniforms.dt, dt);
    gl.uniform1f(advectionProg.uniforms.dissipation, config.DENSITY_DISSIPATION);
    blit(dye.write);
    dye.swap();
  }

  // ---------- Splat ----------

  function doSplat(x, y, dx, dy, color) {
    splatProg.bind();
    gl.uniform1i(splatProg.uniforms.uTarget, velocity.read.attach(0));
    gl.uniform1f(splatProg.uniforms.aspectRatio, canvas.width / canvas.height);
    gl.uniform2f(splatProg.uniforms.point, x, y);
    gl.uniform3f(splatProg.uniforms.color, dx, dy, 0.0);
    gl.uniform1f(splatProg.uniforms.radius, correctRadius(config.SPLAT_RADIUS / 100.0, canvas));
    blit(velocity.write);
    velocity.swap();

    gl.uniform1i(splatProg.uniforms.uTarget, dye.read.attach(0));
    gl.uniform3f(splatProg.uniforms.color, color.r, color.g, color.b);
    blit(dye.write);
    dye.swap();
  }

  function splatPointer() {
    const dx = pointer.deltaX * config.SPLAT_FORCE;
    const dy = pointer.deltaY * config.SPLAT_FORCE;
    doSplat(pointer.texcoordX, pointer.texcoordY, dx, dy, config.COLOR);
  }

  // ---------- Render ----------

  function render() {
    // Clear the canvas to fully transparent black before each frame.
    // Without this, the ONE / ONE_MINUS_SRC_ALPHA blend accumulates alpha
    // from prior frames and darkens the HTML content (video) behind the canvas.
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.clearColor(0.0, 0.0, 0.0, 0.0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.BLEND);
    displayMat.bind(displayKeywords);
    gl.uniform2f(displayMat.uniforms.texelSize, 1.0 / canvas.width, 1.0 / canvas.height);
    gl.uniform1i(displayMat.uniforms.uTexture, dye.read.attach(0));
    blit(null);
  }

  // ---------- Loop ----------

  function loop() {
    if (!state.enabled) { state.rafId = null; return; }
    const now = Date.now();
    let dt = (now - state.lastTime) / 1000;
    state.lastTime = now;
    dt = Math.min(dt, 0.016667);

    if (pointer.moved) {
      pointer.moved = false;
      splatPointer();
    }

    step(dt);
    render();
    state.rafId = requestAnimationFrame(loop);
  }

  state.rafId = requestAnimationFrame(loop);

  // ---------- Handle ----------

  return {
    canvas,
    gl,
    config,
    _state: state,
    _loop: loop,
    _onMouseMove: onMouseMove,
    _onVisibility: onVisibility,
    _onResize: onResize,
  };
}

// ---------- setColor ----------

/**
 * Update the smoke color live (no restart needed — config is read each frame).
 * r, g, b are normalized floats (0–1). Keep values low for subtlety.
 */
export function setColor(handle, r, g, b) {
  if (!handle) return;
  handle.config.COLOR = { r, g, b };
}

// ---------- setEnabled ----------

export function setEnabled(handle, value) {
  if (!handle) return;
  const { _state: state } = handle;
  const was = state.enabled;
  state.enabled = value;

  if (value && !was && !state.rafId && !document.hidden) {
    state.lastTime = Date.now();
    state.rafId = requestAnimationFrame(handle._loop);
  }
}

// ---------- destroy ----------

export function destroy(handle) {
  if (!handle) return;
  handle._state.enabled = false;
  if (handle._state.rafId) {
    cancelAnimationFrame(handle._state.rafId);
    handle._state.rafId = null;
  }
  window.removeEventListener('mousemove', handle._onMouseMove);
  document.removeEventListener('visibilitychange', handle._onVisibility);
  window.removeEventListener('resize', handle._onResize);

  const ext = handle.gl.getExtension('WEBGL_lose_context');
  if (ext) ext.loseContext();
}
